<script setup lang="ts">
import { useTranslation } from "i18next-vue";
import { computed, ref, watch } from "vue";
import CartItemComment from "#/components/cartItemComment/CartItemComment.vue";
import { useAnimationNum } from "#/hooks/useAnimationNum/useAnimationNum";
import { useMainStore } from "#/store/main";
import { useThemeStore } from "#/store/theme";
import { formatPrice, getLocaleAltName, getLocaleName } from "#/utils/i18next";
import {
	getOrderModifierFreeAmount,
	getOrderModifierLineTotal,
	getOrderModifierPaidAmount,
	getOrderModifierPaidUnitPrice,
	getOrderModifierPerPortionTotal,
	shouldShowOrderModifierFormula,
} from "#/utils/orderModifiers";

const props = defineProps<{ cardItem: any; selectable?: boolean }>();

const isSelected = defineModel<boolean>("selected", { default: false });

const { displayNumber: displayPrice, update: updatePrice } = useAnimationNum(0, 800);

const { t, i18next } = useTranslation();
const storeMain = useMainStore();
const themeStore = useThemeStore();
const isExpanded = ref(themeStore.data?.qr_show_modifiers_in_cart ?? false);

const totalPrice = computed(() => props.cardItem?.total_cost);
const isPaid = computed(() => !!props.cardItem?.is_paid);
const dishAmount = computed(() => props.cardItem?.amount ?? 1);
const arrAllModifiers = computed(() => {
	const mods = props.cardItem?.modifiers ?? [];
	const groupMods = (props.cardItem?.group_modifiers ?? []).flatMap((group: any) => group.modifiers ?? []);
	return [...mods, ...groupMods];
});

const isVisibleComment = computed(() => !props.cardItem?.is_combo && !!props.cardItem?.comment?.length && themeStore.data?.comments?.qr_item_comment);

const status = computed(() => {
	if (props.cardItem?.correlation_id) return null;

	const state = props.cardItem?.cooking_state;
	if (!state && state !== 0) return null;

	if (state === 0) return t(`cart.order-box.status.created`);
	if (state === 1) return t(`cart.order-box.status.cooking`);
	if (state === 2) return t(`cart.order-box.status.cooked`);
	if (state === 3) return t(`cart.order-box.status.delivered`);

	return null;
});

const toggleSelected = () => {
	if (!props.selectable || isPaid.value) return;
	isSelected.value = !isSelected.value;
};

const toggleExpand = () => {
	isExpanded.value = !isExpanded.value;
};

const getModifierPaidTotal = (modifier: any) => getOrderModifierPerPortionTotal(modifier);
const getFreeAmount = (modifier: any) => getOrderModifierFreeAmount(modifier);
const getPaidAmount = (modifier: any) => getOrderModifierPaidAmount(modifier);
const getPaidUnitPrice = (modifier: any) => getOrderModifierPaidUnitPrice(modifier);
const getModifierLineTotal = (modifier: any) => getOrderModifierLineTotal(modifier, dishAmount.value);
const shouldShowModifierFormula = (modifier: any) => shouldShowOrderModifierFormula(modifier, dishAmount.value);

// @tolgee-key { key: 'cart-box.free-count', ns: 'translation' }

watch(totalPrice, updatePrice, { immediate: true });
</script>

<template>
	<div class="order_box_wrapper" :class="{ selectable: selectable, selected: isSelected, paid: isPaid }" @click="toggleSelected">
		<div v-if="selectable && !isPaid" class="order_box_checkbox">
			<label class="order_box_checkbox_control" @click.stop>
				<input type="checkbox" v-model="isSelected" :disabled="isPaid" class="order_box_checkbox_input">
				<span class="order_box_checkbox_mark"></span>
			</label>
		</div>
		<img :src="cardItem?.image_link || themeStore.data?.fallback_image || ''" class="order_box_image" crossorigin="anonymous" alt="">
		<div class="order_box_content_wrapper">
			<div class="order_box_content_info">
				<p class="order_box_name">{{ getLocaleName(cardItem, i18next.language) }}</p>
				<p v-if="cardItem?.size" class="order_box_size">{{ getLocaleAltName(cardItem?.size, i18next.language) }}</p>
				<div class="order_box_amount">
					<button disabled type="button">-</button>
					<p class="order_box_amount_count">{{ props.cardItem?.amount }}</p>
					<button disabled type="button">+</button>
				</div>
				<div v-if="status !== null || cardItem?.correlation_id || isPaid" class="order_box_status">
					<span v-if="status !== null" :data-state="cardItem?.cooking_state"> {{ status }} </span>
					<span v-if="cardItem?.correlation_id" data-state="waiting"> {{ t(`cart.order-box.status.waiting-addition`) }} </span>
					<span v-if="isPaid" data-state="paid"> {{ t(`cart.order-box.status.paid`) }} </span>
				</div>
			</div>
			<div class="order_box_info_more">
				<p v-if="props.cardItem?.new_price" :class="{ discount_new_price: props.cardItem?.new_price }">
					{{ formatPrice(props.cardItem.new_price * props.cardItem.amount, storeMain.config_lib.currency_symbol) }}
				</p>
				<p>{{ formatPrice(displayPrice, storeMain.config_lib.currency_symbol) }}</p>
			</div>
		</div>
		<div class="cart_modifier_toggle" v-if="!isExpanded && arrAllModifiers.length > 0" @click.stop="toggleExpand">
			<p class="cart_info_more">{{ t(`order-box.show-modifiers`) }}</p>
			<p class="cart_arrow"></p>
		</div>
		<div v-if="isExpanded && arrAllModifiers.length > 0" class="cart_modifier_info">
			<div class="cart_modifier_title">
				{{ formatPrice(cardItem?.price, storeMain.config_lib.currency_symbol) }}
				× {{ cardItem?.amount }} =
				{{ formatPrice(cardItem?.price * cardItem?.amount, storeMain.config_lib.currency_symbol) }}
			</div>
			<div
				v-for="modifier in arrAllModifiers"
				:key="modifier.id"
				class="cart_modifier_item"
				:class="{ cart_modifier_item_free: getModifierPaidTotal(modifier) === 0 }"
			>
				<div class="cart_modifier_content">
					<p>{{ getLocaleName(modifier, i18next.language) }}</p>
					<div v-if="shouldShowModifierFormula(modifier)" class="cart_modifier_calculation">
						<div v-if="getPaidAmount(modifier) > 0" class="cart_modifier_calculation_line">
							{{ formatPrice(getPaidUnitPrice(modifier), storeMain.config_lib.currency_symbol) }}
							× {{ getPaidAmount(modifier) }} =
							{{ formatPrice(getModifierPaidTotal(modifier), storeMain.config_lib.currency_symbol) }}
							<template v-if="dishAmount > 1">× {{ dishAmount }}</template>
						</div>
						<div v-if="getFreeAmount(modifier) > 0" class="cart_modifier_calculation_line cart_modifier_calculation_free">
							{{ t(`cart-box.free-count`, { count: getFreeAmount(modifier) }) }}
							<template v-if="dishAmount > 1">× {{ dishAmount }}</template>
						</div>
					</div>
				</div>
				<div class="wrapper_price_count_cart">
					{{ formatPrice(getModifierLineTotal(modifier), storeMain.config_lib.currency_symbol) }}
				</div>
			</div>
			<div class="cart_modifier_arrow" @click.stop="toggleExpand">
				<p class="cart_info_more">{{ t(`order-box.hide-modifiers`) }}</p>
				<p class="cart_arrow_top"></p>
			</div>
		</div>

		<CartItemComment v-if="isVisibleComment" :value="cardItem?.comment" />
	</div>
</template>

<style scoped lang="scss">
  @use './orderBox.scss';
</style>
